import {
  buildRhythmTargetId,
  getTabScopedEquipmentData,
} from "@eweadn-qiankun/shared-utils";
import { getIsElectron } from "../../hooks/useElectronEnvironment";
import type { HeartbeatParams } from "@eweadn-qiankun/shared-http";
import {
  ensureXingshanSdkReady,
  serviceKeyboard,
} from "../../sdkApi/keyboard/commonApi";
import { isDeviceSessionStandbyMode } from "./standbyGuard";

/** 子应用 webpack 会编译本链路，勿依赖 `@/` 或 deviceSession.ts */
function readEquipmentData(): Record<string, unknown> | null {
  const tabScoped = getTabScopedEquipmentData();
  let equipment: Record<string, unknown> | null = null;
  if (tabScoped?.solutionRovider) {
    equipment = tabScoped;
  } else if (typeof window !== "undefined") {
    try {
      const raw =
        window.sessionStorage.getItem("eweadn:session:equipmentData") ??
        window.localStorage.getItem("equipmentData");
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      equipment = parsed?.solutionRovider ? parsed : null;
    } catch {
      return null;
    }
  }

  if (!equipment) return null;

  const cachedSn = equipment.sn ?? equipment.serialNumber;
  if (cachedSn != null && String(cachedSn).trim()) {
    return equipment;
  }

  if (typeof window === "undefined") return equipment;
  try {
    const sharedRaw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      window.localStorage.getItem("equipmentData");
    if (!sharedRaw) return equipment;
    const shared = JSON.parse(sharedRaw) as Record<string, unknown>;
    const sharedSn = shared?.sn ?? shared?.serialNumber;
    if (sharedSn != null && String(sharedSn).trim()) {
      return { ...equipment, sn: String(sharedSn).trim() };
    }
  } catch {
    /* ignore */
  }
  return equipment;
}

export function buildDeviceSessionKey(
  equipment: Record<string, unknown>,
  sn?: string | null
): string {
  const xsSn = String(sn ?? "").trim();
  if (xsSn) return `xingshan-sn-${xsSn}`;
  return buildRhythmTargetId(equipment);
}

function persistEquipmentSn(sn: string): void {
  if (typeof window === "undefined") return;
  try {
    const eq = readEquipmentData();
    if (!eq) return;
    const updated = { ...eq, sn };
    window.sessionStorage.setItem(
      "eweadn:session:equipmentData",
      JSON.stringify(updated)
    );
    window.localStorage.setItem("equipmentData", JSON.stringify(updated));
  } catch {
    /* ignore */
  }
}

function readCachedSnFromEquipment(): string | undefined {
  const eq = readEquipmentData();
  const cached = eq?.sn ?? eq?.serialNumber;
  if (cached != null && String(cached).trim()) {
    return String(cached).trim();
  }
  return undefined;
}

function isXingshanInputReportNotReadyError(e: unknown): boolean {
  const msg = String((e as Error)?.message ?? e ?? "");
  return (
    msg.includes("InputReportManager") ||
    msg.includes("未初始化") ||
    msg.includes("not initialized")
  );
}

export async function readXingshanSn(): Promise<string | undefined> {
  const cached = readCachedSnFromEquipment();
  if (isDeviceSessionStandbyMode()) {
    return cached;
  }
  /** 心跳不必每次读 HID；equipment 已有 SN 时直接复用 */
  if (cached) {
    return cached;
  }

  try {
    await ensureXingshanSdkReady();
    const kb = serviceKeyboard as {
      getDevicesInfo?: () => Promise<{ sn?: string } | null>;
    };
    if (typeof kb.getDevicesInfo !== "function") return undefined;
    const info = await kb.getDevicesInfo();
    const sn = info?.sn;
    if (sn != null && String(sn).trim()) {
      const normalized = String(sn).trim();
      persistEquipmentSn(normalized);
      return normalized;
    }
  } catch (e) {
    if (isXingshanInputReportNotReadyError(e)) {
      console.warn(
        "[device-session] readXingshanSn SDK 未就绪，跳过 HID 读 SN"
      );
      return readCachedSnFromEquipment();
    }
  }
  return undefined;
}

export async function buildXingshanHeartbeatParams(
  sessionId: string
): Promise<HeartbeatParams | null> {
  const equipment = readEquipmentData();
  if (!equipment || equipment.solutionRovider !== "xingshan") {
    return null;
  }

  const sn = await readXingshanSn();
  const vid = equipment.vendorId;
  const pid = equipment.productId;

  return {
    sessionId,
    deviceKey: buildDeviceSessionKey(equipment, sn),
    sn,
    vid: vid != null ? `0x${Number(vid).toString(16)}` : undefined,
    pid: pid != null ? `0x${Number(pid).toString(16)}` : undefined,
    deviceType: "keyboard",
    solutionProvider: "xingshan",
    clientType: getIsElectron() ? "desktop" : "web",
    deviceName:
      String(equipment.displayname ?? equipment.productName ?? "").trim() ||
      undefined,
  };
}
